import {
    parseLoadSubsetOptions,
    queryCollectionOptions,
} from "@tanstack/query-db-collection";
import {
    collectionOptions,
    type DbClient,
    type LiveQueryOptions,
    inArray as tsInArray,
    useDbClient,
    useLiveQuery,
} from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { z } from "zod";
import { getCatalog } from "@/lib/core/catalog-core.server";
import {
    ProviderBatchInputSchema,
    ProviderProjectionsSchema,
} from "./provider.contract";

export const getProvidersBatchData = createServerOnlyFn(
    async (data: { organizationIds: string[] }) => {
        const catalog = await getCatalog();
        const providers = await catalog.organizations.getOrganizationsByIds(
            data.organizationIds,
        );
        return ProviderProjectionsSchema.parse(
            providers.map((provider) => ({
                id: provider.id,
                isVerified: provider.isVerified,
                type: provider.type,
                name: provider.name,
                locations: provider.locations,
                policies: provider.policies,
                socialUrls: provider.socialUrls,
                missionStatement: provider.missionStatement,
                primaryPhotoId: provider.primaryPhotoId,
                website: provider.website,
                inBusinessSince: provider.inBusinessSince,
                source: provider.source,
                kennel_name: provider.kennel_name,
                breeder_name: provider.breeder_name,
                coverPhotoUrl: provider.coverPhotoUrl,
                hasAkcRegistration: provider.hasAkcRegistration,
                description: provider.description,
                city: provider.city,
                state: provider.state,
                location: provider.location,
                phone: provider.phone,
                email: provider.email,
                media: provider.media.map((media) => ({
                    id: media.id,
                    description: media.description,
                    mimeType: media.mimeType,
                    source: media.source,
                    sourceId: media.sourceId,
                    url: media.url,
                })),
                breedNames: provider.breedNames,
                breedSlugs: provider.breedSlugs,
            })),
        );
    },
);

export const getProviderHeadData = createServerOnlyFn(
    async (organizationId: string) => {
        const catalog = await getCatalog();
        return await catalog.organizations.getOrganizationHead(organizationId);
    },
);

export const getProviderSharePreview = createServerOnlyFn(
    async (organizationId: string) => {
        const catalog = await getCatalog().catch(() => null);
        if (!catalog) return null;
        try {
            return await catalog.sharePreviews.getMedia({
                kind: "organization",
                id: organizationId,
            });
        } catch {
            return null;
        }
    },
);

const getProvidersBatch = createServerFn({ method: "POST" })
    .inputValidator((data) => ProviderBatchInputSchema.parse(data))
    .handler(async ({ data }) => getProvidersBatchData(data));

type WagsOrganizationBatchRecord = Awaited<
    ReturnType<typeof getProvidersBatchData>
>[number];

export const providersCollection = collectionOptions(
    "wagsOrganizations",
    (dbClient) =>
        queryCollectionOptions<WagsOrganizationBatchRecord>({
            id: "wagsOrganizations",
            queryKey: ["wagsOrganizations"],
            queryClient: dbClient.requireDependency<QueryClient>("queryClient"),
            getKey: (item) => item.id,
            syncMode: "on-demand",
            queryFn: async (ctx) => {
                const { filters } = parseLoadSubsetOptions(
                    ctx.meta?.loadSubsetOptions,
                );
                const idFilter = filters.find(
                    (filter) =>
                        filter.field.join(".") === "id" &&
                        filter.operator === "in",
                );
                const organizationIds =
                    z.array(z.string()).safeParse(idFilter?.value).data ?? [];

                if (organizationIds.length === 0) {
                    return [];
                }

                return getProvidersBatch({
                    data: { organizationIds },
                });
            },
        }),
);

export function getProvidersCollection(dbClient: DbClient) {
    return dbClient.collection(providersCollection);
}

const WAGS_ORGANIZATIONS_COLLECTION_GC_TIME = 5 * 60 * 1000;

function getWagsOrganizationsLiveQueryOptions(organizationIds: string[]) {
    const normalizedOrganizationIds = [
        ...new Set(organizationIds.filter(Boolean)),
    ].sort();

    return {
        gcTime: WAGS_ORGANIZATIONS_COLLECTION_GC_TIME,
        queryKey: ["wagsOrganizations", "subset", normalizedOrganizationIds],
        query: (queryBuilder) =>
            queryBuilder
                .from({ organizations: providersCollection })
                .where(({ organizations }) =>
                    tsInArray(organizations.id, normalizedOrganizationIds),
                ),
    } satisfies LiveQueryOptions;
}

export async function prefetchProviders(
    dbClient: DbClient,
    organizationIds: string[],
) {
    if (!organizationIds.some(Boolean)) {
        return;
    }

    if (import.meta.env?.DEV && typeof window !== "undefined") {
        getProvidersCollection(dbClient);
    }

    await dbClient.preloadLiveQuery(
        getWagsOrganizationsLiveQueryOptions(organizationIds),
    );
}

export const useProviders = (organizationIds: string[]) => {
    const dbClient = useDbClient();
    if (import.meta.env?.DEV && typeof window !== "undefined") {
        getProvidersCollection(dbClient);
    }

    const query = useLiveQuery(
        getWagsOrganizationsLiveQueryOptions(organizationIds),
    );

    const data = (() => {
        if (query.data == null) {
            return query.data;
        }

        const orgById = new Map<string, (typeof query.data)[number]>(
            query.data.map((org) => [org.id, org]),
        );

        return organizationIds.flatMap((orgId) => {
            const org = orgById.get(orgId);
            return org ? [org] : [];
        });
    })();

    return { ...query, data };
};

export type ProviderProjectionRecord = NonNullable<
    ReturnType<typeof useProviders>["data"]
>[number];
